/**
 * Shared create/edit validation schemas for admin, tutor, student, parent, volunteer, and session forms.
 */

import { ValidationSchema } from '@/hooks/useFormValidation';
import { EMAIL_FORMAT_REGEX, SCHOOL_YEAR_CODE_PATTERN } from '@/constants/shared-catalog';
import { getErrorMessage } from '@/utils/errors-utils';
import { getPhoneFieldError } from '@/utils/phone-formats-utils';
import { isRemoteValuedEngagementRequired } from '@/utils/professional-situation-utils';
import {
  checkCustomEngagementDurationError,
  checkEngagementStartNotInPast,
  getTutorMinAgeError as getRemoteTutorMinAgeError,
} from '@/components/tutors/remote/tutor-form-utils';
import { getTutorMinAgeError as getInPersonTutorMinAgeError } from '@/components/tutors/inperson/tutor-form-utils';
import { TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS } from '@/components/tutors/tutor-configs';
import {
  VISIO_BACKGROUND_ACCEPTED_FORMATS,
  VISIO_BACKGROUND_MAX_SIZE_BYTES,
} from '@/components/visio-backgrounds/visio-background-configs';

/** Minimal school-year shape for overlap / code-conflict checks. */
type SchoolYearRange = {
  id?: string;
  code?: string;
  startDate: Date | string;
  endDate: Date | string;
};

/** Form fields read to resolve a contact phone country. */
type ContactPhoneCountrySource = {
  contactCountryCode?: unknown;
  secondaryContactCountryCode?: unknown;
  _phoneCountryCode?: unknown;
};

/** Detected contact kind used by format checks. */
type ContactValueKind = 'email' | 'phone' | 'unknown';

/** Trim a school-year code (`2025-2026`). */
function normalizeSchoolYearCode(code: string): string {
  return code.trim();
}

/** `YYYY-MM-DD` from a Date or ISO string. */
function toSchoolYearDateOnly(value: Date | string): string {
  if (typeof value === 'string') {
    return value.trim().slice(0, 10);
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** French `DD/MM/YYYY` for overlap error copy. */
function formatSchoolYearDateFr(value: Date | string): string {
  const ymd = toSchoolYearDateOnly(value);
  const [year, month, day] = ymd.split('-');
  if (!year || !month || !day) return ymd;
  return `${day}/${month}/${year}`;
}

/** Error when the code is not `YYYY-YYYY` with end after start. */
function getSchoolYearCodeFormatError(code: string): string | undefined {
  const normalized = normalizeSchoolYearCode(code);
  if (!SCHOOL_YEAR_CODE_PATTERN.test(normalized)) {
    return getErrorMessage('schoolYearCodeFormat');
  }
  const [from, to] = normalized.split('-').map(Number);
  if (from >= to) {
    return getErrorMessage('schoolYearCodeEndAfterStart');
  }
  return undefined;
}

/** True when two inclusive date ranges overlap. */
function schoolYearRangesOverlap(
  aStart: Date | string,
  aEnd: Date | string,
  bStart: Date | string,
  bEnd: Date | string,
): boolean {
  const as = toSchoolYearDateOnly(aStart);
  const ae = toSchoolYearDateOnly(aEnd);
  const bs = toSchoolYearDateOnly(bStart);
  const be = toSchoolYearDateOnly(bEnd);
  return as <= be && bs <= ae;
}

/** Other school years, excluding the one being edited. */
function getOtherSchoolYears(
  years: SchoolYearRange[] | undefined,
  excludeId?: string,
): SchoolYearRange[] {
  return (years ?? []).filter((year) => year.id !== excludeId);
}

/** Error when another year already uses this code. */
function getSchoolYearCodeConflictError(
  code: string,
  others: SchoolYearRange[],
): string | undefined {
  const normalized = normalizeSchoolYearCode(code);
  if (!SCHOOL_YEAR_CODE_PATTERN.test(normalized)) return undefined;
  const taken = others.some((year) => year.code === normalized);
  if (!taken) return undefined;
  return getErrorMessage({ code: 'schoolYearCodeTaken', yearCode: normalized });
}

/** Error when the date range overlaps another school year. */
function getSchoolYearOverlapError(
  startDate: string | undefined,
  endDate: string | undefined,
  others: SchoolYearRange[],
): string | undefined {
  if (!startDate || !endDate) return undefined;
  if (toSchoolYearDateOnly(endDate) <= toSchoolYearDateOnly(startDate)) return undefined;
  const overlapping = others.find((year) =>
    schoolYearRangesOverlap(startDate, endDate, year.startDate, year.endDate),
  );
  if (!overlapping) return undefined;
  const label = overlapping.code ? `l'année ${overlapping.code}` : 'une autre année scolaire';
  return getErrorMessage({
    code: 'schoolYearDatesOverlap',
    label,
    start: formatSchoolYearDateFr(overlapping.startDate),
    end: formatSchoolYearDateFr(overlapping.endDate),
  });
}

/** UI requires branch fields (establishment, employer, …) that Nest completion does not. */
function missingHighSchoolFields(ps: Record<string, unknown>): string[] {
  const missing: string[] = [];
  if (!ps.establishment && !ps.establishmentName) missing.push("l'établissement scolaire");
  if (!ps.level) missing.push("le niveau d'études");
  if (!ps.curriculum) missing.push('le cursus scolaire');
  return missing;
}

/** Missing labels for a higher-ed student situation. */
function missingStudentFields(ps: Record<string, unknown>): string[] {
  const missing: string[] = [];
  if (!ps.establishment && !ps.establishmentName) missing.push("l'établissement d'étude");
  if (!ps.level) missing.push("le niveau d'études");
  return missing;
}

/** Missing labels for employee / civil-servant situation. */
function missingEmployeeFields(ps: Record<string, unknown>, orgLabel: string): string[] {
  const missing: string[] = [];
  if (!ps.fieldOfActivity) missing.push("le secteur d'activité");
  if (!ps.employerId && !ps.employerName && !ps.companyName) missing.push(orgLabel);
  if (!ps.activityDepartment) missing.push("le département d'activité");
  return missing;
}

/** Error listing empty required fields for the selected situation status. */
function validateProfessionalSituationSubFields(
  value: unknown,
  persona: 'tutor' | 'parent',
): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const ps = value as Record<string, unknown>;
  const status = (ps.status as string | undefined) ?? '';

  let missing: string[] = [];
  if (status === 'high_school_student') {
    missing = missingHighSchoolFields(ps);
  } else if (status === 'student') {
    missing = missingStudentFields(ps);
  } else if (persona === 'tutor' && (status === 'employee' || status === 'entrepreneur')) {
    missing = missingEmployeeFields(ps, "l'entreprise");
  } else if (persona === 'tutor' && status === 'civil_servant') {
    missing = missingEmployeeFields(ps, "l'organisme / administration");
  }

  if (missing.length > 0) {
    return `Veuillez renseigner : ${missing.join(', ')}`;
  }
  if (
    persona === 'tutor' &&
    isRemoteValuedEngagementRequired(status) &&
    !ps.associativeEngagement
  ) {
    return getErrorMessage('valuedEngagementRequired');
  }
  return undefined;
}

/** Tutor professional-situation branch fields. */
const professionalSituationTutorRule = {
  required: false,
  custom: (value: unknown) => validateProfessionalSituationSubFields(value, 'tutor'),
};

/** ISO used to check a contact phone: field country, then `_phoneCountryCode`, else `FR`. */
function contactPhoneCountry(
  formData: ContactPhoneCountrySource | undefined,
  field: 'contactCountryCode' | 'secondaryContactCountryCode',
): string {
  const explicit = formData?.[field];
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  const fallback = formData?._phoneCountryCode;
  if (typeof fallback === 'string' && fallback.trim()) return fallback.trim();
  return 'FR';
}

/** Email regex, or phone-by-country via getPhoneFieldError. `unknown` → contactInvalid. */
function contactValueError(
  value: string,
  kind: ContactValueKind,
  countryCode?: string,
): string | undefined {
  if (kind === 'email') {
    if (!EMAIL_FORMAT_REGEX.test(value)) return getErrorMessage('invalidEmail');
    return undefined;
  }
  if (kind === 'phone') {
    return getPhoneFieldError(value, countryCode || 'FR');
  }
  return getErrorMessage('contactInvalid');
}

/** Primary contact. Shared by create and edit person forms. */
const contactRule = {
  required: true,
  dependsOn: ['contactCountryCode', 'contactType'],
  custom: (value: any, formData?: any) => {
    if (!value?.trim()) {
      return getErrorMessage('contactRequired');
    }

    const type = formData?._contactType as ContactValueKind | undefined;
    if (type === 'email' || type === 'phone' || type === 'unknown') {
      const formatError = contactValueError(
        value,
        type,
        contactPhoneCountry(formData, 'contactCountryCode'),
      );
      if (formatError) return formatError;
    }

    if (formData?._contactExists === true) {
      return getErrorMessage(type === 'email' ? 'emailAlreadyExists' : 'phoneAlreadyExists');
    }

    return undefined;
  },
};

/** Optional secondary contact. Kind is the opposite of `contactType`. */
const secondaryContactRule = {
  required: false,
  dependsOn: ['contactType', 'secondaryContactCountryCode'],
  custom: (value: any, formData?: any) => {
    if (!value?.trim()) return undefined;

    const primaryType = formData?.contactType;
    const secondaryType: ContactValueKind = primaryType === 'email' ? 'phone' : 'email';
    const formatError = contactValueError(
      value,
      secondaryType,
      contactPhoneCountry(formData, 'secondaryContactCountryCode'),
    );
    if (formatError) return formatError;

    if (formData?._secondaryContactExists === true) {
      return getErrorMessage(
        secondaryType === 'email' ? 'emailAlreadyExists' : 'phoneAlreadyExists',
      );
    }

    return undefined;
  },
};

/**
 * Engagement end vs start (YYYY-MM-DD, lexicographic).
 * - allowSameDay=false: end strictly after start (DateSplit minDate = start+1)
 * - allowSameDay=true: end >= start ({@link DatePicker})
 */
/** Remote tutor: end required, after start, and at least 3 calendar months later. */
const remoteTutorEngagementEndDateRule = {
  required: true,
  dependsOn: ['engagementStartDate'],
  custom: (value: any, formData?: any) => {
    if (!value || value === '') return getErrorMessage('engagementEndRequired');
    const start = formData?.engagementStartDate as string | undefined;
    if (!start) return undefined;
    if (value <= start) return getErrorMessage('engagementEndAfterStart');
    return checkCustomEngagementDurationError(start, value, TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS);
  },
};

const engagementEndAfterStartRule = (options?: { required?: boolean; allowSameDay?: boolean }) => {
  const required = options?.required ?? true;
  const allowSameDay = options?.allowSameDay ?? false;
  return {
    required,
    dependsOn: ['engagementStartDate'],
    custom: (value: any, formData?: any) => {
      if (!value || value === '') {
        return required ? getErrorMessage('engagementEndRequired') : undefined;
      }
      const start = formData?.engagementStartDate as string | undefined;
      if (!start) return undefined;
      if (allowSameDay ? value < start : value <= start) {
        return allowSameDay
          ? getErrorMessage('engagementEndBeforeStart')
          : getErrorMessage('engagementEndAfterStart');
      }
      return undefined;
    },
  };
};

/**
 * Remote student contact: required for minors; optional for adults (parent contact is used).
 * Minors skip format (inherited, locked). Adults with a value use {@link contactValueError}.
 */
const studentRemoteContactRule = {
  required: false,
  dependsOn: ['contactCountryCode', 'contactType'],
  custom: (value: any, formData?: any) => {
    const isAdult: boolean = !!formData?._isAdult;

    if (!isAdult) {
      if (!value?.trim()) return getErrorMessage('contactRequired');
      return undefined;
    }

    if (!value?.trim()) return undefined;

    const type = (formData?._contactType ?? 'unknown') as ContactValueKind;
    const formatError = contactValueError(
      value,
      type === 'email' || type === 'phone' || type === 'unknown' ? type : 'unknown',
      contactPhoneCountry(formData, 'contactCountryCode'),
    );
    if (formatError) return formatError;

    if (formData?._contactExists === true) {
      return getErrorMessage(type === 'email' ? 'emailAlreadyExists' : 'phoneAlreadyExists');
    }

    return undefined;
  },
};

/** In-person student create / edit. */
export const studentValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 20,
    custom: (value) => {
      if (typeof value === 'string' && value.trim().length === 1)
        return getErrorMessage('nameMinTwoChars');
      return undefined;
    },
  },
  dateOfBirth: {
    required: false,
  },
  gender: {
    required: true,
  },
  levelId: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'string' && value.trim() === ''))
        return getErrorMessage('academicLevelRequired');
      return undefined;
    },
  },
  establishmentIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneEstablishment');
      }
      return undefined;
    },
  },
  displayName: {
    maxLength: 50,
  },
  addressLine1: {
    maxLength: 255,
  },
  department: {
    maxLength: 100,
  },
  postalCode: {
    maxLength: 20,
  },
  city: {
    maxLength: 100,
  },
  subjects: {
    required: false,
  },
  availability: {
    required: false,
  },
  medicalNotes: {
    maxLength: 500,
  },
  exitDate: {
    required: false,
    custom: (value, formData) => {
      if (formData?.status === 'deactivated' && (!value || value === '')) {
        return getErrorMessage('exitDateRequired');
      }
      return undefined;
    },
  },
};

/** Legacy teacher form (email + phone + subjects). */
export const teacherValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  email: {
    required: true,
    email: true,
  },
  phone: {
    required: true,
    phone: true,
  },
  dateOfBirth: {
    required: true,
    custom: (value) => {
      if (!value) return undefined;
      const date = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear();
      if (age < 18 || age > 70) {
        return getErrorMessage('ageRange18to70');
      }
      return undefined;
    },
  },
  gender: {
    required: true,
  },
  subjectIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneSubject');
      }
      return undefined;
    },
  },
  address: {
    maxLength: 200,
  },
  emergencyContact: {
    phone: true,
  },
  notes: {
    maxLength: 500,
  },
};

/** Admin invite (email or phone depending on `inviteBy`). */
export const adminValidationSchema: ValidationSchema = {
  firstName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  email: {
    email: true,
    dependsOn: ['inviteBy'],
    custom: (value, formData) => {
      if (formData?.inviteBy === 'email' && !value) {
        return getErrorMessage('emailRequired');
      }
      return undefined;
    },
  },
  phone: {
    phone: true,
    dependsOn: ['inviteBy'],
    custom: (value, formData) => {
      if (formData?.inviteBy === 'phone' && !value) {
        return getErrorMessage('phoneRequired');
      }
      return undefined;
    },
  },
  roleIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneRole');
      }
      return undefined;
    },
  },
  profileId: {
    required: false,
  },
  dateOfBirth: {
    custom: (value) => {
      if (!value) return undefined;
      const date = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear();
      if (age < 18 || age > 70) {
        return getErrorMessage('ageRange18to70');
      }
      return undefined;
    },
  },
  gender: {
    required: false,
  },
  establishmentIds: {
    required: false,
  },
};

/** Admin create (unified contact). */
export const adminCreateValidationSchema: ValidationSchema = {
  firstName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  roleIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneRole');
      }
      return undefined;
    },
  },
  establishmentIds: {
    required: false,
  },
  residenceDepartmentIds: {
    custom: (value, formData) => {
      if (formData?._requiresResidenceDepartments) {
        if (!Array.isArray(value) || value.length === 0) {
          return getErrorMessage('residenceDepartmentRequiredForManager');
        }
      }
      return undefined;
    },
  },
};

/** Admin edit (phone optional, no strict format). */
export const adminEditValidationSchema: ValidationSchema = {
  firstName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  email: {
    required: false,
    email: true,
  },
  phone: {
    required: false,
    phone: true,
  },
  roleIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneRole');
      }
      return undefined;
    },
  },
  establishmentIds: {
    required: false,
  },
  residenceDepartmentIds: {
    custom: (value, formData) => {
      if (formData?._requiresResidenceDepartments) {
        if (!Array.isArray(value) || value.length === 0) {
          return getErrorMessage('residenceDepartmentRequiredForManager');
        }
      }
      return undefined;
    },
  },
};

/** School-class create / edit. */
export const classroomValidationSchema: ValidationSchema = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
  },
  levelId: {
    required: true,
  },
  capacity: {
    required: true,
    min: 1,
    max: 100,
  },
  mainTeacherId: {
    required: false,
  },
};

/** School-level catalogue create / edit. */
export const levelValidationSchema: ValidationSchema = {
  code: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Z0-9_-]+$/,
  },
  label: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  minAge: {
    min: 3,
    max: 25,
  },
  maxAge: {
    min: 3,
    max: 25,
    dependsOn: ['minAge'],
    custom: (value, formData) => {
      if (!value || !formData?.minAge) return undefined;
      const minAge = Number(formData.minAge);
      const maxAge = Number(value);
      if (maxAge < minAge) {
        return getErrorMessage('maxAgeAfterMinAge');
      }
      return undefined;
    },
  },
};

/** Subject catalogue create / edit. */
export const subjectValidationSchema: ValidationSchema = {
  code: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Z0-9_-]+$/,
  },
  label: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  description: {
    maxLength: 500,
  },
};

/** Program catalogue create / edit. */
export const programValidationSchema: ValidationSchema = {
  code: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Z0-9_-]+$/,
  },
  name: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  description: {
    maxLength: 500,
  },
  // Optional — only validate order when both dates are set
  startDate: {
    required: false,
  },
  endDate: {
    required: false,
    dependsOn: ['startDate'],
    custom: (value, formData) => {
      if (!value || !formData?.startDate) return undefined;
      const start = new Date(formData.startDate as string);
      const end = new Date(value as string);
      if (end < start) {
        return getErrorMessage('endDateOnOrAfterStart');
      }
      return undefined;
    },
  },
  coursePathType: {
    required: true,
  },
};

/** School-year catalogue create / edit (code + non-overlapping dates). */
export const schoolYearValidationSchema: ValidationSchema = {
  code: {
    required: true,
    minLength: 4,
    maxLength: 20,
    pattern: SCHOOL_YEAR_CODE_PATTERN,
    custom: (value, formData) => {
      const code = String(value ?? '').trim();
      const formatError = getSchoolYearCodeFormatError(code);
      if (formatError && SCHOOL_YEAR_CODE_PATTERN.test(code)) return formatError;
      const others = getOtherSchoolYears(
        formData?._existingYears as SchoolYearRange[] | undefined,
        formData?._excludeId as string | undefined,
      );
      return getSchoolYearCodeConflictError(code, others);
    },
  },
  startDate: {
    required: true,
    dependsOn: ['endDate'],
    custom: (_value, formData) => {
      const others = getOtherSchoolYears(
        formData?._existingYears as SchoolYearRange[] | undefined,
        formData?._excludeId as string | undefined,
      );
      return getSchoolYearOverlapError(
        formData?.startDate as string | undefined,
        formData?.endDate as string | undefined,
        others,
      );
    },
  },
  endDate: {
    required: true,
    dependsOn: ['startDate'],
    custom: (value, formData) => {
      if (!value || !formData?.startDate) return undefined;
      if (String(value) <= String(formData.startDate)) {
        return getErrorMessage('endDateAfterStart');
      }
      const others = getOtherSchoolYears(
        formData?._existingYears as SchoolYearRange[] | undefined,
        formData?._excludeId as string | undefined,
      );
      return getSchoolYearOverlapError(
        formData.startDate as string | undefined,
        value as string | undefined,
        others,
      );
    },
  },
};

/** Holiday / vacation create / edit. */
export const holidayValidationSchema: ValidationSchema = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  startDate: {
    required: true,
  },
  endDate: {
    required: true,
    dependsOn: ['startDate'],
    custom: (value, formData) => {
      if (!value || !formData?.startDate) return undefined;
      const start = new Date(formData.startDate);
      const end = new Date(value);
      if (end < start) {
        return getErrorMessage('endDateOnOrAfterStart');
      }
      return undefined;
    },
  },
  description: {
    maxLength: 500,
  },
  establishmentId: {
    required: false,
  },
  vacationZoneId: {
    required: false,
  },
};

/** Activity catalogue create / edit. */
export const activityValidationSchema: ValidationSchema = {
  title: {
    required: true,
    minLength: 3,
    maxLength: 100,
  },
  description: {
    required: true,
    minLength: 10,
    maxLength: 1000,
  },
  activityType: {
    required: true,
  },
  date: {
    required: true,
  },
  startTime: {
    required: true,
  },
  endTime: {
    required: true,
    dependsOn: ['startTime'],
    custom: (value, formData) => {
      if (!value || !formData?.startTime) return undefined;
      if (value <= formData.startTime) {
        return getErrorMessage('endTimeAfterStart');
      }
      return undefined;
    },
  },
  location: {
    maxLength: 200,
  },
  maxParticipants: {
    min: 1,
    max: 1000,
  },
  levelIds: {
    custom: (value) => {
      if (value && Array.isArray(value) && value.length === 0) {
        return undefined;
      }
      return undefined;
    },
  },
  classroomIds: {
    custom: (value) => {
      if (value && Array.isArray(value) && value.length === 0) {
        return undefined;
      }
      return undefined;
    },
  },
};

/** Timetable slot create / edit. */
export const timetableValidationSchema: ValidationSchema = {
  classroomId: {
    required: true,
  },
  subjectId: {
    required: true,
  },
  teacherId: {
    required: true,
  },
  dayOfWeek: {
    required: true,
    min: 0,
    max: 6,
  },
  startTime: {
    required: true,
  },
  endTime: {
    required: true,
    dependsOn: ['startTime'],
    custom: (value, formData) => {
      if (!value || !formData?.startTime) return undefined;
      if (value <= formData.startTime) {
        return getErrorMessage('endTimeAfterStart');
      }
      return undefined;
    },
  },
  room: {
    maxLength: 50,
  },
};

/** Grade entry create / edit. */
export const gradeValidationSchema: ValidationSchema = {
  studentId: {
    required: true,
  },
  subjectId: {
    required: true,
  },
  evaluationType: {
    required: true,
  },
  score: {
    required: true,
    min: 0,
    max: 20,
  },
  coefficient: {
    min: 0.5,
    max: 5,
  },
  date: {
    required: true,
  },
  comments: {
    maxLength: 500,
  },
};

/** Attendance mark create / edit. */
export const attendanceValidationSchema: ValidationSchema = {
  studentId: {
    required: true,
  },
  date: {
    required: true,
  },
  status: {
    required: true,
  },
  reason: {
    maxLength: 500,
  },
};

/** Login form (identifier + password). */
export const loginValidationSchema: ValidationSchema = {
  identifier: {
    required: true,
    minLength: 3,
  },
  password: {
    required: true,
    minLength: 6,
  },
};

/** Personal post-it create / edit. */
export const postItValidationSchema: ValidationSchema = {
  title: {
    required: true,
    minLength: 1,
    maxLength: 255,
  },
  thematic: {
    required: true,
  },
  content: {
    required: true,
    minLength: 1,
    maxLength: 1000,
  },
  deadline: {
    required: false,
    custom: (value) => {
      if (!value) return undefined;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return getErrorMessage('dateTimeInvalid');
      }
      const totalMinutes = date.getHours() * 60 + date.getMinutes();
      const minMinutes = 8 * 60;
      const maxMinutes = 18 * 60;
      if (totalMinutes < minMinutes || totalMinutes > maxMinutes) {
        return getErrorMessage('deadlineOutsideHours');
      }
      return undefined;
    },
  },
  color: {
    required: false,
  },
  isPriority: {
    required: false,
  },
};

/** Activity-type catalogue create / edit. */
export const activityTypeValidationSchema: ValidationSchema = {
  code: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Z0-9_-]+$/,
  },
  label: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  description: {
    maxLength: 500,
  },
};

/** Activity config (capacity / tutors) create / edit. */
export const activityConfigValidationSchema: ValidationSchema = {
  code: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[A-Z0-9_-]+$/,
  },
  label: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  description: {
    maxLength: 500,
  },
  activityTypeId: {
    required: true,
  },
  maxTutors: {
    min: 0,
    max: 100,
  },
  maxStudents: {
    min: 0,
    max: 100,
  },
  maxVolunteers: {
    min: 0,
    max: 100,
  },
  maxStudentsPerTutor: {
    min: 0,
    max: 100,
  },
};

/** In-person session create / edit. */
export const sessionInPersonValidationSchema: ValidationSchema = {
  establishmentId: {
    required: true,
  },
  roomId: {
    required: false,
  },
  programId: {
    required: false,
  },
  levelIds: {
    required: false,
  },
  subjectIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneSubject');
      }
      return undefined;
    },
  },
  classId: {
    required: false,
  },
  specialtyIds: {
    required: false,
  },
  activityId: {
    required: false,
  },
  sessionDate: {
    required: true,
  },
  startTime: {
    required: true,
  },
  endTime: {
    required: true,
    dependsOn: ['startTime'],
    custom: (value, formData) => {
      if (!value || !formData?.startTime) return undefined;
      if (value <= formData.startTime) {
        return getErrorMessage('endTimeAfterStart');
      }
      return undefined;
    },
  },
  type: {
    required: true,
  },
  sessionFormat: {
    required: true,
  },
  mode: {
    required: true,
    dependsOn: ['recurrenceType'],
    custom: (value, formData) => {
      if (!value) return getErrorMessage('sessionModeRequired');
      if (
        value === 'recurring' &&
        (!formData?.recurrenceType || formData.recurrenceType === 'none')
      ) {
        return getErrorMessage('recurrenceTypeRequiredForRecurring');
      }
      return undefined;
    },
  },
  recurrenceType: {
    required: false,
    dependsOn: ['mode'],
    custom: (value, formData) => {
      if (formData?.mode === 'recurring' && (!value || value === 'none')) {
        return getErrorMessage('recurrenceTypeRequired');
      }
      return undefined;
    },
  },
  recurrenceRule: {
    required: false,
    dependsOn: ['recurrenceType'],
    custom: (value, formData) => {
      if (formData?.recurrenceType === 'several_days_per_week') {
        const weekDays = value?.weekDays;
        if (!Array.isArray(weekDays) || weekDays.length === 0) {
          return getErrorMessage('weekDaysRequired');
        }
      }
      return undefined;
    },
  },
  topic: {
    required: false,
    maxLength: 500,
  },
  joinLink: {
    required: false,
    url: true,
    dependsOn: ['sessionFormat'],
    custom: (value, formData) => {
      if (formData?.sessionFormat === 'remote' && !value) {
        return getErrorMessage('visioLinkRequired');
      }
      if (value && !/^https?:\/\/.+/.test(value)) {
        return getErrorMessage('visioLinkInvalid');
      }
      return undefined;
    },
  },
  internalNotes: {
    required: false,
    maxLength: 1000,
  },
  planDuringHolidays: {
    required: false,
  },
};

/** In-person session report create / edit. */
export const reportValidationSchema: ValidationSchema = {
  subject: {
    required: true,
  },
  comment: {
    required: true,
    maxLength: 1000,
  },
  rating: {
    required: true,
    min: 1,
    max: 5,
  },
  feeling: {
    required: true,
  },
};

/** Volunteer invite (legacy split contact fields). */
export const volunteerValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  email: {
    email: true,
    dependsOn: ['inviteBy'],
    custom: (value, formData) => {
      if (formData?.inviteBy === 'email' && !value) {
        return getErrorMessage('emailRequired');
      }
      return undefined;
    },
  },
  phone: {
    phone: true,
    dependsOn: ['inviteBy'],
    custom: (value, formData) => {
      if (formData?.inviteBy === 'phone' && !value) {
        return getErrorMessage('phoneRequired');
      }
      return undefined;
    },
  },
  displayName: {
    maxLength: 50,
  },
  establishmentIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneEstablishment');
      }
      return undefined;
    },
  },
  managedById: {
    required: false,
  },
  profileId: {
    required: false,
  },
  subjects: {
    required: false,
  },
  availability: {
    required: false,
  },
};

/** Volunteer create (unified contact). */
export const volunteerCreateValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  displayName: {
    maxLength: 50,
  },
  roleIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneRole');
      }
      return undefined;
    },
  },
  establishmentIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneEstablishment');
      }
      return undefined;
    },
  },
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  managedById: {
    required: false,
  },
  subjects: {
    required: false,
  },
  availability: {
    required: false,
  },
  engagementStartDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('engagementStartRequired');
      return undefined;
    },
  },
  engagementEndDate: engagementEndAfterStartRule({ allowSameDay: false }),
};

/** Volunteer edit (phone optional, no strict format). */
export const volunteerEditValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  displayName: {
    maxLength: 50,
  },
  contact: contactRule,
  email: {
    required: false,
    email: true,
  },
  phone: {
    required: false,
    phone: true,
  },
  establishmentIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneEstablishment');
      }
      return undefined;
    },
  },
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  managedById: {
    required: false,
  },
  subjects: {
    required: false,
  },
  availability: {
    required: false,
  },
  engagementStartDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('engagementStartRequired');
      return undefined;
    },
  },
  engagementEndDate: engagementEndAfterStartRule({ allowSameDay: false }),
  exitDate: {
    required: false,
    custom: (value, formData) => {
      if (formData?.status === 'deactivated' && (!value || value === '')) {
        return getErrorMessage('effectiveExitDateRequired');
      }
      return undefined;
    },
  },
};

/** Remote tutor create. */
export const tutorRemoteCreateValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  dateOfBirth: {
    required: false,
    custom: (value) => getRemoteTutorMinAgeError(typeof value === 'string' ? value : undefined),
  },
  acceptSharePhone: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('phoneShareRequired');
      }
      return undefined;
    },
  },
  tutoringType: {
    required: false,
  },
  programs: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneProgram');
      }
      return undefined;
    },
  },
  coursePath: {
    required: false,
  },
  numberOfStudentsToSupport: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('studentsToSupportRequired');
      return undefined;
    },
  },
  engagementStartDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('startDateRequired');
      return checkEngagementStartNotInPast(typeof value === 'string' ? value : undefined);
    },
  },
  engagementEndDate: remoteTutorEngagementEndDateRule,
  level: {
    required: true,
    custom: (value) => {
      if (!value || (Array.isArray(value) ? value.length === 0 : value === ''))
        return getErrorMessage('levelRequired');
      return undefined;
    },
  },
  subjectToTeach: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('teachingSubjectRequired');
      return undefined;
    },
  },
  availableOnWeekends: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('weekendAvailabilityRequired');
      }
      return undefined;
    },
  },
  availableOnHolidays: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('holidayAvailabilityRequired');
      }
      return undefined;
    },
  },
  availableOnVacations: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('vacationAvailabilityRequired');
      }
      return undefined;
    },
  },
  availability: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return getErrorMessage('availabilitiesRequired');
      }
      return undefined;
    },
  },
  valorisation: {
    required: false,
  },
  criminalRecordDocumentLink: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('criminalRecordRequired');
      return undefined;
    },
  },
  contractDocumentLink: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('contractDocumentRequired');
      return undefined;
    },
  },
  professionalSituation: professionalSituationTutorRule,
};

/** Remote tutor edit (`basic_information` step). */
export const tutorRemoteEditValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  gender: {
    required: false,
  },
  dateOfBirth: {
    required: false,
    custom: (value) => getRemoteTutorMinAgeError(typeof value === 'string' ? value : undefined),
  },
  acceptSharePhone: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('phoneShareRequired');
      }
      return undefined;
    },
  },
  tutoringType: {
    required: false,
  },
  programs: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneProgram');
      }
      return undefined;
    },
  },
  coursePath: {
    required: false,
  },
  numberOfStudentsToSupport: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('studentsToSupportRequired');
      return undefined;
    },
  },
  engagementStartDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('startDateRequired');
      return undefined;
    },
  },
  engagementEndDate: remoteTutorEngagementEndDateRule,
  level: {
    required: true,
    custom: (value) => {
      if (!value || (Array.isArray(value) ? value.length === 0 : value === ''))
        return getErrorMessage('levelRequired');
      return undefined;
    },
  },
  subjectToTeach: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('teachingSubjectRequired');
      return undefined;
    },
  },
  availableOnWeekends: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('weekendAvailabilityRequired');
      }
      return undefined;
    },
  },
  availableOnHolidays: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('holidayAvailabilityRequired');
      }
      return undefined;
    },
  },
  availableOnVacations: {
    required: true,
    custom: (value) => {
      if (value === undefined || value === null) {
        return getErrorMessage('vacationAvailabilityRequired');
      }
      return undefined;
    },
  },
  availability: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return getErrorMessage('availabilitiesRequired');
      }
      return undefined;
    },
  },
  valorisation: {
    required: false,
  },
  criminalRecordDocumentLink: {
    required: false,
    custom: (value, formData) => {
      const cr = (formData as any)?.criminalRecord;
      const hasExisting = !!(cr?.fileName || cr?.documentName);
      if (!hasExisting && (!value || value === '')) {
        return getErrorMessage('criminalRecordRequired');
      }
      return undefined;
    },
  },
  contractDocumentLink: {
    required: false,
    custom: (value, formData) => {
      const hasExisting = !!(
        (formData as any)?.contractDocumentName || (formData as any)?.contractUploadDate
      );
      if (!hasExisting && (!value || value === '')) {
        return getErrorMessage('contractDocumentRequired');
      }
      return undefined;
    },
  },
  professionalSituation: professionalSituationTutorRule,
};

/**
 * Error when the information-meeting date is missing or outside [Jan 1 current year, today].
 * `allowHistorical` keeps an already stored past date on edit (the picker cannot select it).
 */
function getInformationMeetingParticipationDateError(
  value: unknown,
  formData: { hasParticipated?: boolean } | undefined,
  allowHistorical: boolean,
): string | undefined {
  if (!formData?.hasParticipated) return undefined;
  if (value == null || value === '') {
    return getErrorMessage('informationMeetingParticipationDateRequired');
  }
  if (typeof value !== 'string') {
    return getErrorMessage('informationMeetingParticipationDateInvalid');
  }
  const raw = value;
  if (Number.isNaN(new Date(raw).getTime())) {
    return getErrorMessage('informationMeetingParticipationDateInvalid');
  }
  const day = raw.slice(0, 10);
  const today = new Date().toLocaleDateString('en-CA');
  const yearStart = `${today.slice(0, 4)}-01-01`;
  if (allowHistorical && day < yearStart) return undefined;
  if (day < yearStart || day > today) {
    return getErrorMessage('informationMeetingParticipationDateOutOfRange');
  }
  return undefined;
}

/** Schema rule for `participatedAt` on in-person tutor forms. */
function informationMeetingParticipationDateRule(allowHistorical: boolean) {
  return {
    custom: (value: unknown, formData?: { hasParticipated?: boolean }) =>
      getInformationMeetingParticipationDateError(value, formData, allowHistorical),
    dependsOn: ['hasParticipated'],
  };
}

/** In-person tutor create. */
export const tutorInPersonCreateValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  address: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('addressRequired');
      return undefined;
    },
  },
  zipCode: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('postalCodeRequired');
      return undefined;
    },
  },
  city: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('cityRequired');
      return undefined;
    },
  },
  department: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('departmentRequired');
      return undefined;
    },
  },
  dateOfBirth: {
    required: false,
    custom: (value) => getInPersonTutorMinAgeError(typeof value === 'string' ? value : undefined),
  },
  professionalSituation: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('professionalSituationRequired');
      return undefined;
    },
  },
  tutoringType: {
    required: false,
  },
  subjects: {
    required: true,
    custom: (value) => {
      if (!value || (Array.isArray(value) ? value.length === 0 : value === ''))
        return getErrorMessage('teachingSubjectRequired');
      return undefined;
    },
  },
  engagementStartDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('startDateRequired');
      return undefined;
    },
  },
  engagementEndDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('engagementEndRequired');
      return undefined;
    },
  },
  availability: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return getErrorMessage('availabilitiesRequired');
      }
      return undefined;
    },
  },
  criminalRecordDocumentLink: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('criminalRecordRequired');
      return undefined;
    },
  },
  contractDocumentLink: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('contractDocumentRequired');
      return undefined;
    },
  },
  participatedAt: informationMeetingParticipationDateRule(false),
};

/** In-person tutor edit. */
export const tutorInPersonEditValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  address: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('addressRequired');
      return undefined;
    },
  },
  zipCode: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('postalCodeRequired');
      return undefined;
    },
  },
  city: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('cityRequired');
      return undefined;
    },
  },
  department: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('departmentRequired');
      return undefined;
    },
  },
  dateOfBirth: {
    required: false,
    custom: (value) => getInPersonTutorMinAgeError(typeof value === 'string' ? value : undefined),
  },
  professionalSituation: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('professionalSituationRequired');
      return undefined;
    },
  },
  subjects: {
    required: true,
    custom: (value) => {
      if (!value || (Array.isArray(value) ? value.length === 0 : value === ''))
        return getErrorMessage('teachingSubjectRequired');
      return undefined;
    },
  },
  engagementStartDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('startDateRequired');
      return undefined;
    },
  },
  engagementEndDate: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('engagementEndRequired');
      return undefined;
    },
  },
  availability: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return getErrorMessage('availabilitiesRequired');
      }
      return undefined;
    },
  },
  criminalRecordDocumentLink: {
    required: false,
    custom: (value, formData) => {
      const cr = (formData as any)?.criminalRecord;
      const hasExisting = !!(cr?.fileName || cr?.documentName);
      if (!hasExisting && (!value || value === '')) {
        return getErrorMessage('criminalRecordRequired');
      }
      return undefined;
    },
  },
  contractDocumentLink: {
    required: false,
    custom: (value, formData) => {
      const hasExisting = !!(
        (formData as any)?.contractDocumentName || (formData as any)?.contractUploadDate
      );
      if (!hasExisting && (!value || value === '')) {
        return getErrorMessage('contractDocumentRequired');
      }
      return undefined;
    },
  },
  participatedAt: informationMeetingParticipationDateRule(true),
};

/** Six-digit numeric login code. */
const loginCodeRule = {
  required: true,
  custom: (value: any) => {
    if (!value || value === '') return getErrorMessage('loginCodeRequired');
    if (!/^\d+$/.test(value)) return getErrorMessage('loginCodeNumeric');
    if (value.length !== 6) return getErrorMessage('loginCodeSixDigits');
    return undefined;
  },
};

/** Remote student create. */
export const studentRemoteCreateValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: studentRemoteContactRule,
  secondaryContact: secondaryContactRule,
  dateOfBirth: {
    required: true,
  },
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  code: loginCodeRule,
  address: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('addressRequired');
      return undefined;
    },
  },
  zipCode: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('postalCodeRequired');
      return undefined;
    },
  },
  city: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('cityRequired');
      return undefined;
    },
  },
  department: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('departmentRequired');
      return undefined;
    },
  },
  schoolSituation: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('schoolSituationRequired');
      return undefined;
    },
  },
  level: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('levelRequired');
      return undefined;
    },
  },
  schoolEstablishment: {
    required: true,
    custom: (value) => {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return getErrorMessage('schoolEstablishmentRequired');
      }
      return undefined;
    },
  },
  availability: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return getErrorMessage('availabilitiesRequired');
      }
      return undefined;
    },
  },
  programs: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneProgram');
      }
      return undefined;
    },
  },
  subject: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('subjectRequired');
      return undefined;
    },
  },
};

/** Remote student edit (`registration_information` step). */
export const studentRemoteEditValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: studentRemoteContactRule,
  secondaryContact: secondaryContactRule,
  dateOfBirth: {
    required: true,
  },
  gender: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('genderRequired');
      return undefined;
    },
  },
  code: loginCodeRule,
  address: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('addressRequired');
      return undefined;
    },
  },
  zipCode: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('postalCodeRequired');
      return undefined;
    },
  },
  city: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('cityRequired');
      return undefined;
    },
  },
  department: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('departmentRequired');
      return undefined;
    },
  },
  schoolSituation: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('schoolSituationRequired');
      return undefined;
    },
  },
  level: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('levelRequired');
      return undefined;
    },
  },
  schoolEstablishment: {
    required: true,
    custom: (value) => {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return getErrorMessage('schoolEstablishmentRequired');
      }
      return undefined;
    },
  },
  availability: {
    required: true,
    custom: (value) => {
      if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return getErrorMessage('availabilitiesRequired');
      }
      return undefined;
    },
  },
  programs: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneProgram');
      }
      return undefined;
    },
  },
  subject: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('subjectRequired');
      return undefined;
    },
  },
};

/** Parent create. */
export const parentCreateValidationSchema: ValidationSchema = {
  gender: {
    required: true,
  },
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  accessCode: loginCodeRule,
  address: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('addressRequired');
      return undefined;
    },
  },
  zipCode: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('postalCodeRequired');
      return undefined;
    },
  },
  city: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('cityRequired');
      return undefined;
    },
  },
  departmentId: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('departmentRequired');
      return undefined;
    },
  },
  country: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('countryRequired');
      return undefined;
    },
  },
  dateOfBirth: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('birthDateRequired');
      return undefined;
    },
  },
  professionalSituation: {
    required: true,
    custom: (value) => {
      const status =
        typeof value === 'object' && value !== null ? (value as { status?: string }).status : value;
      if (!status || status === '') return getErrorMessage('professionalSituationRequired');
      return validateProfessionalSituationSubFields(value, 'parent');
    },
  },
  contractDocumentLink: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('engagementDocumentRequired');
      return undefined;
    },
  },
};

/** Parent edit. */
export const parentEditValidationSchema: ValidationSchema = {
  gender: {
    required: true,
  },
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  contact: contactRule,
  secondaryContact: secondaryContactRule,
  dateOfBirth: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('birthDateRequired');
      return undefined;
    },
  },
  // In edit mode the admin may not know the parent's access code — only require it if
  // the field is explicitly touched (the parent already has one saved in the DB).
  accessCode: {
    required: false,
    custom: (value) => {
      if (!value || value === '') return undefined; // blank = leave existing code unchanged
      if (!/^\d+$/.test(value)) return 'Le code de connexion doit être uniquement numérique (0–9)';
      if (value.length !== 6) return 'Le code de connexion doit faire exactement 6 chiffres';
      return undefined;
    },
  },
  address: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('addressRequired');
      return undefined;
    },
  },
  zipCode: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('postalCodeRequired');
      return undefined;
    },
  },
  city: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('cityRequired');
      return undefined;
    },
  },
  department: {
    required: false,
  },
  departmentId: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('departmentRequired');
      return undefined;
    },
  },
  country: {
    required: true,
    custom: (value) => {
      if (!value || value === '') return getErrorMessage('countryRequired');
      return undefined;
    },
  },
  professionalSituation: {
    required: true,
    custom: (value) => {
      const status =
        typeof value === 'object' && value !== null ? (value as { status?: string }).status : value;
      if (!status || status === '') return getErrorMessage('professionalSituationRequired');
      return validateProfessionalSituationSubFields(value, 'parent');
    },
  },
  contractDocumentLink: {
    // In edit mode, a parent may have signed via the mobile app (cgu + engagement + signature)
    // without a contractDocumentLink — do not block editing if a contract already exists.
    required: false,
    custom: (value, formData) => {
      const hasExistingContract = !!(
        (formData as any)?.contractDocumentName ||
        (formData as any)?.contractUploadDate ||
        (formData as any)?.contractSignedDate
      );
      if (!hasExistingContract && (!value || value === '')) {
        return getErrorMessage('engagementDocumentRequired');
      }
      return undefined;
    },
  },
};

/** Establishment create / edit. */
export const establishmentValidationSchema: ValidationSchema = {
  uaiCode: {
    required: false,
    minLength: 7,
    maxLength: 8,
    pattern: /^\d{7}[A-Z]$/,
  },
  name: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  officialName: {
    maxLength: 255,
  },
  type: {
    required: true,
  },
  status: {
    required: false,
  },
  addressLine1: {
    maxLength: 255,
  },
  department: {
    maxLength: 100,
  },
  postalCode: {
    maxLength: 20,
  },
  city: {
    maxLength: 100,
  },
  country: {
    maxLength: 2,
    pattern: /^[A-Z]{2}$/,
  },
  timezone: {
    maxLength: 50,
  },
  contactName: {
    maxLength: 255,
  },
  contactEmail: {
    email: true,
    maxLength: 255,
  },
  contactPhone: {
    phone: true,
    maxLength: 50,
  },
  studentQuota: {
    min: 0,
    max: 10000,
  },
  volunteerQuota: {
    min: 0,
    max: 10000,
  },
  volunteersToRecruit: {
    min: 0,
    max: 10000,
  },
  managedById: {
    required: false,
  },
};

/** Signed-in user profile edit. */
export const profileEditValidationSchema: ValidationSchema = {
  firstName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  lastName: {
    required: false,
    minLength: 2,
    maxLength: 100,
  },
  displayName: {
    required: false,
    maxLength: 50,
  },
  contact: contactRule,
  email: {
    required: false,
    email: true,
  },
  phone: {
    required: false,
    phone: true,
  },
  status: {
    required: false,
  },
};

/** Optional contact; format is checked only when the field is filled. */
const optionalContactRule = {
  required: false,
  dependsOn: ['contactCountryCode', 'contactType'],
  custom: (value: any, formData?: any) => {
    if (!value?.trim()) return undefined;
    const type = formData?._contactType as ContactValueKind | undefined;
    if (type !== 'email' && type !== 'phone' && type !== 'unknown') return undefined;
    return contactValueError(value, type, contactPhoneCountry(formData, 'contactCountryCode'));
  },
};

/** Walk-in event participant: names required, contacts optional with format check. */
export const unregisteredStudentValidationSchema: ValidationSchema = {
  firstName: {
    required: true,
  },
  lastName: {
    required: true,
    maxLength: 2,
  },
  primaryContact: optionalContactRule,
  secondaryContact: secondaryContactRule,
};

/** Field / company event create / edit. */
export const eventValidationSchema: ValidationSchema = {
  establishmentId: {
    required: true,
  },
  eventDate: {
    required: true,
  },
  eventEndDate: {
    required: false,
    dependsOn: ['eventDate'],
    custom: (value, formData) => {
      if (!value || !formData?.eventDate) return undefined;
      const startDate = new Date(formData.eventDate as string);
      const endDate = new Date(value as string);
      if (endDate < startDate) {
        return getErrorMessage('endDateSameOrAfterStart');
      }
      return undefined;
    },
  },
  startTime: {
    required: true,
  },
  endTime: {
    required: true,
    dependsOn: ['startTime'],
    custom: (value, formData) => {
      if (!value || !formData?.startTime) return undefined;
      if (value <= formData.startTime) {
        return getErrorMessage('endTimeAfterStart');
      }
      return undefined;
    },
  },
  activityId: {
    required: false,
  },
  activityIds: {
    required: true,
    custom: (value) => {
      if (!Array.isArray(value) || value.length === 0) {
        return getErrorMessage('atLeastOneActivity');
      }
      return undefined;
    },
  },
  eventFormat: {
    required: true,
  },
  mode: {
    required: true,
    dependsOn: ['recurrenceType'],
    custom: (value, formData) => {
      if (!value) return getErrorMessage('eventModeRequired');
      if (
        value === 'recurring' &&
        (!formData?.recurrenceType || formData.recurrenceType === 'none')
      ) {
        return getErrorMessage('eventRecurrenceTypeRequired');
      }
      return undefined;
    },
  },
  recurrenceType: {
    required: false,
    dependsOn: ['mode'],
    custom: (value, formData) => {
      if (formData?.mode === 'recurring' && (!value || value === 'none')) {
        return getErrorMessage('eventRecurrenceTypeMandatory');
      }
      return undefined;
    },
  },
  recurrenceRule: {
    required: false,
    dependsOn: ['recurrenceType'],
    custom: (value, formData) => {
      if (formData?.recurrenceType === 'several_days_per_week') {
        const weekDays = value?.weekDays;
        if (!Array.isArray(weekDays) || weekDays.length === 0) {
          return getErrorMessage('weekDaysRequired');
        }
      }
      return undefined;
    },
  },
  joinLink: {
    required: false,
    url: true,
    dependsOn: ['eventFormat'],
    custom: (value, formData) => {
      if (formData?.eventFormat === 'remote' && !value) {
        return getErrorMessage('eventVisioLinkRequired');
      }
      if (value && !/^https?:\/\/.+/.test(value)) {
        return getErrorMessage('visioLinkInvalid');
      }
      return undefined;
    },
  },
  internalNotes: {
    required: false,
    maxLength: 1000,
  },
  planDuringHolidays: {
    required: false,
  },
};

/** Remote session create ({@link CreateSessionView}); rules depend on sessionType and matching prefill. */
export const createSessionRemoteValidationSchema: ValidationSchema = {
  selectedType: {
    required: true,
    custom: (v) => (!v ? getErrorMessage('sessionTypeRequired') : undefined),
  },
  sessionDate: {
    required: true,
    custom: (v) => (!v ? getErrorMessage('sessionDateRequired') : undefined),
  },
  startTime: {
    required: true,
    custom: (v) => (!v ? getErrorMessage('startTimeRequired') : undefined),
  },
  tutorId: {
    custom: (v, fd) =>
      fd?._sessionType === 'tutoring' && !v ? getErrorMessage('tutorRequired') : undefined,
  },
  studentId: {
    custom: (v, fd) =>
      fd?._sessionType === 'tutoring' && !v ? getErrorMessage('studentRequired') : undefined,
  },
  managedById: {
    custom: (v, fd) =>
      fd?._sessionType === 'tutoring' && !v ? getErrorMessage('managedByRequired') : undefined,
  },
  recurrenceType: {
    custom: (v, fd) => {
      const sessionType = fd?._sessionType;
      const requiresRecurrence =
        sessionType === 'tutoring' ||
        sessionType === 'information-meeting' ||
        sessionType === 'welcome-meeting';
      return requiresRecurrence && !v ? getErrorMessage('recurrenceRequired') : undefined;
    },
  },
  endTime: {
    custom: (v, fd) =>
      fd?._sessionType === 'tutoring' && !v ? getErrorMessage('endTimeRequired') : undefined,
  },
  programId: {
    custom: (v, fd) =>
      fd?._sessionType === 'tutoring' && !fd?._isPrefilledFromMatching && !v
        ? getErrorMessage('programRequired')
        : undefined,
  },
  eventType: {
    custom: (v, fd) =>
      fd?._sessionType === 'field-event' && !v ? getErrorMessage('eventTypeRequired') : undefined,
  },
  animatorsCount: {
    custom: (_, fd) => {
      if (fd?._sessionType === 'information-meeting' && (fd?._animatorsCount ?? 0) === 0)
        return getErrorMessage('atLeastOneFacilitator');
      if (fd?._sessionType === 'welcome-meeting' && !fd?._singleAnimatorId)
        return getErrorMessage('facilitatorRequired');
      return undefined;
    },
  },
};

/** Remote session edit ({@link EditSessionView}). */
export const editSessionRemoteValidationSchema: ValidationSchema = {
  sessionDate: {
    required: true,
    custom: (v) => (!v ? getErrorMessage('sessionDateRequired') : undefined),
  },
  startTime: {
    required: true,
    custom: (v) => (!v ? getErrorMessage('startTimeRequired') : undefined),
  },
  managedById: {
    custom: (v, fd) => (fd?._isTutoring && !v ? getErrorMessage('managedByRequired') : undefined),
  },
  programId: {
    custom: (v, fd) => (fd?._isTutoring && !v ? getErrorMessage('programRequired') : undefined),
  },
};

/** Archive a remote user (reason + comment when `other`). */
export const archiveRemoteValidationSchema: ValidationSchema = {
  reason: { required: true, dependsOn: ['comment'] },
  comment: {
    maxLength: 200,
    custom: (value, formData) => {
      if (formData?.reason !== 'other') return undefined;
      if (!String(value ?? '').trim()) return getErrorMessage('specifyReason');
      return undefined;
    },
  },
};

/** Cancel a session (reason + detail when `OTHER`). */
export const cancelSessionValidationSchema: ValidationSchema = {
  reasonCode: {
    required: true,
    custom: (value) => (!value ? getErrorMessage('cancelReasonRequired') : undefined),
    dependsOn: ['reasonDetail'],
  },
  reasonDetail: {
    maxLength: 1000,
    custom: (value, formData) => {
      if (formData?.reasonCode !== 'OTHER') return undefined;
      if (!String(value ?? '').trim()) return getErrorMessage('cancelCommentRequired');
      return undefined;
    },
  },
};

/** Dissociate a pair (reason + description when `OTHER`). */
export const dissociationValidationSchema: ValidationSchema = {
  reason: { required: true, dependsOn: ['description'] },
  description: {
    custom: (value, formData) => {
      if (formData?.reason !== 'OTHER') return undefined;
      if (!String(value ?? '').trim()) return getErrorMessage('specifyReason');
      return undefined;
    },
  },
};

/** Cancel an event (reason required). */
export const cancelEventValidationSchema: ValidationSchema = {
  reason: {
    required: true,
    custom: (value) => (!value ? getErrorMessage('cancelReasonRequired') : undefined),
  },
};

/** Reject a visio-background file that is not JPEG, PNG, or WEBP, over the size cap, or flagged by the dropzone. */
function visioBackgroundFileError(
  value: unknown,
  formData?: { hasExistingImage?: boolean; fileRejected?: boolean },
): string | undefined {
  if (!(value instanceof File)) {
    if (formData?.fileRejected) {
      return getErrorMessage({ code: 'fileFormatRejected', hint: 'JPG, PNG ou WEBP' });
    }
    if (formData?.hasExistingImage) return undefined;
    return getErrorMessage('fieldRequired');
  }
  if (value.size > VISIO_BACKGROUND_MAX_SIZE_BYTES) {
    return getErrorMessage({
      code: 'fileTooLarge',
      maxMb: Math.round(VISIO_BACKGROUND_MAX_SIZE_BYTES / 1024 / 1024),
    });
  }
  const tokens = new Set(
    VISIO_BACKGROUND_ACCEPTED_FORMATS.split(',').map((token) => token.trim().toLowerCase()),
  );
  const extension = `.${value.name.split('.').pop()?.toLowerCase() ?? ''}`;
  const extensionOk = tokens.has(extension);
  const mimeOk = tokens.has(value.type.toLowerCase());
  if (!extensionOk && !mimeOk) {
    return getErrorMessage({ code: 'fileFormatRejected', hint: 'JPG, PNG ou WEBP' });
  }
  if (formData?.fileRejected) {
    return getErrorMessage({ code: 'invalidFileType', fileKind: 'JPG, PNG ou WEBP' });
  }
  return undefined;
}

/** Shared name rule for visio-background create and edit. */
const visioBackgroundNameRule = {
  required: true,
  maxLength: 120,
  custom: (value: unknown) => {
    if (typeof value !== 'string' || value.trim().length === 0) {
      return getErrorMessage('fieldRequired');
    }
    return undefined;
  },
};

/** Visio-background create. File is required and must be a JPEG, PNG, or WEBP. */
export const visioBackgroundCreateValidationSchema: ValidationSchema = {
  name: visioBackgroundNameRule,
  file: {
    required: true,
    custom: (value, formData) => visioBackgroundFileError(value, formData),
  },
};

/** Visio-background edit. A picked file must be valid; the stored image counts when none is picked. */
export const visioBackgroundEditValidationSchema: ValidationSchema = {
  name: visioBackgroundNameRule,
  file: {
    required: false,
    custom: (value, formData) => visioBackgroundFileError(value, formData),
  },
};
